import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
  Pagination,
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useAlertVisuals } from "../../context/AlertVisualContext";
import { alertAccentSx, getAlertAccent } from "../../components/alerts/alertVisualPreferences";

const getApiBase = () => {
  const configured = process.env.REACT_APP_API_BASE;
  if (!configured) return "";
  return configured.replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const VigilanteDashboard = () => {
  const theme = useTheme();
  const { settings: alertSettings } = useAlertVisuals();
  const [data, setData] = useState({ cameras: [], events: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [alertsPage, setAlertsPage] = useState(1);
  const alertsPerPage = 6;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/vigilante/dashboard?limit=30`, {
        headers: authHeaders(),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.message || "No se pudo cargar el monitoreo");
      setData({
        cameras: Array.isArray(payload.cameras) ? payload.cameras : [],
        events: Array.isArray(payload.events) ? payload.events : [],
        summary: payload.summary || {},
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const cards = useMemo(
    () => [
      {
        label: "Camaras",
        value: data.summary.total_cameras || 0,
        icon: <CameraAltOutlinedIcon />,
        color: "#38bdf8",
      },
      {
        label: "Online",
        value: data.summary.online_cameras || 0,
        icon: <SecurityOutlinedIcon />,
        color: "#22c55e",
      },
      {
        label: "Alertas",
        value: data.summary.total_alerts || 0,
        icon: <WarningAmberOutlinedIcon />,
        color: "#f59e0b",
      },
    ],
    [data.summary]
  );

  const paginatedEvents = useMemo(() => {
    const start = (alertsPage - 1) * alertsPerPage;
    const end = start + alertsPerPage;

    return data.events.slice(start, end);
  }, [data.events, alertsPage]);

  const totalAlertPages = Math.ceil(data.events.length / alertsPerPage);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100%" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
        <Box>
          <Typography variant="h1" color="text.primary">
            Monitoreo
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Camaras y alertas asignadas por el administrador.
          </Typography>
        </Box>
        <IconButton
          onClick={fetchData}
          sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} mb={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                background: theme.palette.background.paper,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h3" color="text.primary" mt={0.5}>
                    {card.value}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CameraAltOutlinedIcon color="primary" />
              <Typography variant="h5">Camaras en vivo</Typography>
            </Box>

            <Grid container spacing={2}>
              {!loading && data.cameras.length === 0 && (
                <Grid item xs={12}>
                  <Typography color="text.secondary">No hay camaras asignadas.</Typography>
                </Grid>
              )}

              {loading && data.cameras.length === 0 && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}

              {data.cameras.map((camera) => (
                <Grid item xs={12} sm={6} key={`${camera.source}-${camera.id}-${camera.name}`}>
                  <Box sx={{ borderRadius: "8px", overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ position: "relative", pt: "56.25%", background: "#000" }}>
                      <img
                        src={`/mjpeg_feed/${encodeURIComponent(camera.name)}`}
                        alt={camera.name}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = "https://placehold.co/640x360/111827/38bdf8?text=Camara+Offline";
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: 8,
                          top: 8,
                          px: 1,
                          py: 0.4,
                          borderRadius: 1,
                          bgcolor: (theme) => theme.custom.surface.base,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>
                          {camera.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              height: "fit-content",
              minHeight: 420,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <WarningAmberOutlinedIcon color="warning" />
              <Typography variant="h5">Alertas recientes</Typography>
            </Box>

            {!loading && data.events.length === 0 && (
              <Typography color="text.secondary">
                No hay alertas recientes para estas camaras.
              </Typography>
            )}

            <Box
              sx={{
                maxHeight: 560,
                overflowY: "auto",
                pr: 1,
                pb: 1,

                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "8px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(15,23,42,0.25)",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Stack spacing={1.5}>
                {paginatedEvents.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: "divider",
                      borderLeft: `3px solid ${getAlertAccent(event.alert_type, theme.palette.mode, alertSettings)}`,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.035)"
                          : "rgba(15,23,42,0.035)",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {String(event.alert_type || "Evento").replace(/_/g, " ")}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {event.camera_name || "Camara"}
                        </Typography>
                      </Box>

                      <Chip
                        label={event.alert_level || "INFO"}
                        sx={alertAccentSx(getAlertAccent(event.alert_type, theme.palette.mode, alertSettings))}
                        color={
                          ["HIGH", "CRITICAL"].includes(
                            String(event.alert_level || "").toUpperCase()
                          )
                            ? "error"
                            : "warning"
                        }
                        size="small"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {event.alert_timestamp
                        ? new Date(event.alert_timestamp).toLocaleString("es-AR")
                        : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {totalAlertPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalAlertPages}
                  page={alertsPage}
                  onChange={(_, value) => setAlertsPage(value)}
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VigilanteDashboard;
